import React, { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

/**
 * Editorial-styled modal showing real-world examples for a given package.
 * Closes on Esc, backdrop click, and the X button.
 */
export default function PackageExamplesModal({ open, onClose, packageName, examples, heroImage, heroAlt }) {
    const closeBtnRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        // lock body scroll while modal is open
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        // focus the close button for accessibility
        setTimeout(() => closeBtnRef.current?.focus(), 50);
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [open, onClose]);

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    onClick={onClose}
                    data-testid="examples-modal-backdrop"
                    className="fixed inset-0 z-[60] bg-brand-dark/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="examples-modal-title"
                >
                    <motion.div
                        initial={{ y: 60, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 60, opacity: 0 }}
                        transition={{ duration: 0.35, ease: "easeOut" }}
                        onClick={(e) => e.stopPropagation()}
                        data-testid="examples-modal"
                        className="bg-brand-cream text-brand-dark w-full sm:max-w-2xl max-h-[88vh] overflow-y-auto relative shadow-2xl"
                    >
                        {/* Header */}
                        <div className="sticky top-0 bg-brand-cream/95 backdrop-blur-sm border-b border-brand-border-light px-6 sm:px-10 py-5 flex items-start justify-between gap-6 z-10">
                            <div>
                                <div className="flex items-center gap-3 mb-2">
                                    <span className="h-px w-8 bg-brand-orange" />
                                    <span className="text-[10px] uppercase tracking-[0.3em] text-brand-orange font-medium">
                                        Example Concepts
                                    </span>
                                </div>
                                <h3
                                    id="examples-modal-title"
                                    data-testid="examples-modal-title"
                                    className="font-serif text-2xl sm:text-3xl leading-tight"
                                >
                                    {packageName}
                                </h3>
                            </div>
                            <button
                                ref={closeBtnRef}
                                type="button"
                                onClick={onClose}
                                data-testid="examples-modal-close"
                                aria-label="Close examples"
                                className="shrink-0 w-10 h-10 border border-brand-border-light flex items-center justify-center hover:border-brand-orange hover:text-brand-orange transition-colors"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Hero photo */}
                        {heroImage && (
                            <div
                                data-testid="examples-modal-hero"
                                className="relative w-full overflow-hidden bg-brand-dark"
                            >
                                <img
                                    src={heroImage}
                                    alt={heroAlt || packageName}
                                    loading="lazy"
                                    decoding="async"
                                    style={{ imageRendering: "high-quality" }}
                                    className="w-full h-64 sm:h-80 object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/30 to-transparent pointer-events-none" />
                            </div>
                        )}

                        {/* Body */}
                        <div className="px-6 sm:px-10 py-8 space-y-8">
                            <p className="text-brand-muted text-base leading-relaxed">
                                A few example concepts we&apos;ve developed for clients —
                                each tailored to the venue, the season, and the
                                revenue goal.
                            </p>

                            <ul className="space-y-7">
                                {examples.map((ex, i) => (
                                    <li
                                        key={ex.title}
                                        data-testid={`example-item-${i}`}
                                        className="border-t border-brand-border-light pt-7 first:border-t-0 first:pt-0"
                                    >
                                        <div className="flex items-baseline gap-4 mb-2">
                                            <span className="font-serif italic text-brand-orange text-xl">
                                                0{i + 1}
                                            </span>
                                            <h4 className="font-serif text-xl sm:text-2xl leading-snug">
                                                {ex.title}
                                            </h4>
                                        </div>
                                        <p className="text-brand-muted leading-relaxed pl-9">
                                            {ex.body}
                                        </p>
                                        {ex.tag && (
                                            <span className="inline-block mt-3 ml-9 text-[10px] uppercase tracking-[0.25em] text-brand-orange border border-brand-orange px-3 py-1">
                                                {ex.tag}
                                            </span>
                                        )}
                                    </li>
                                ))}
                            </ul>

                            <div className="pt-6 border-t border-brand-border-light flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:justify-between">
                                <p className="text-sm text-brand-muted max-w-sm">
                                    Have a concept in mind? Bring it to a discovery
                                    call and we&apos;ll shape it together.
                                </p>
                                <a
                                    href="#contact"
                                    onClick={onClose}
                                    data-testid="examples-modal-cta"
                                    className="inline-flex items-center gap-2 bg-brand-orange text-brand-cream px-6 py-3 text-xs uppercase tracking-[0.25em] hover:bg-brand-orange-hover transition-colors"
                                >
                                    Start the conversation
                                </a>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
